import { useSessionBlocked, useSessionTransition } from '@/stores/sessionTransition';
import { ReceiptComposerHost } from '@/v2/components/financial/receipt/ReceiptComposerHost';
import { sessionPaused, useSessionRecovery } from '@/stores/sessionRecovery';
import { SessionRecoveryDialog } from './SessionRecoveryDialog';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Loader2, ShieldAlert } from 'lucide-react';
import type { User } from '@valiosascrm/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useDraftUnloadWarning } from '@/hooks/useDraftUnloadWarning';
import { useRealtimeClinicSettings } from '@/hooks/useRealtimeClinicSettings';
import { useSocketToken } from '@/hooks/useSocketToken';
import { useWorkDrafts } from '@/stores/workDrafts';
import { useAuthStore } from '@/stores/auth';
import { useInboxStore } from '@/stores/useInboxStore';
import { clearLegacyLocation } from '@/lib/tenant-context';
import { toast } from '@/v2/design-system/primitives';
import { changeCookieSession, endCookieSession, SupersededSessionError } from '@/v2/utils/auth-flow';
import { landingFor } from '@/v2/utils/landing';
import { useConfirmDiscardWork } from './useConfirmDiscardWork';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { DateFixNoticeBanner } from './DateFixNoticeBanner';
import { DocumentSetupNoticeBanner } from './DocumentSetupNoticeBanner';
import { UnitsSetupNoticeBanner } from './UnitsSetupNoticeBanner';
import { WhatsAppUpgradeNoticeBanner } from './WhatsAppUpgradeNoticeBanner';
import { WhatsAppDisconnectedNoticeBanner } from './WhatsAppDisconnectedNoticeBanner';
import { ActiveCampaignBanner } from './ActiveCampaignBanner';
import { useSingleFlightRunner } from '@/hooks/useSingleFlightCallback';

function ImpersonationBanner() {
  const discardWork = useConfirmDiscardWork();
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  if (!user?.impersonation) return null;

  const end = async () => {
    try {
      if (await endCookieSession(queryClient, '/auth/impersonation/end')) navigate('/admin', { replace: true });
    } catch {
      toast.error('Não foi possível encerrar o acesso assistido. Verifique a conexão e tente novamente.');
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-so-14 text-amber-900 lg:px-8">
      {discardWork.confirmation}
      <ShieldAlert className="size-4 shrink-0" />
      <span className="font-bold">Acesso assistido ativo</span>
      <span className="text-amber-800">
        Você está operando como {user.name}. Todas as ações são auditadas.
      </span>
      <button
        type="button"
        onClick={() => discardWork.run(() => { void end(); })}
        className="ml-auto rounded-so-sm bg-amber-900 px-3 py-1.5 text-so-12 font-bold text-white hover:bg-amber-800"
      >
        Encerrar
      </button>
    </div>
  );
}

function PendingInvitationsBanner() {
  // Story 65.37: a gravação desta tela roda uma vez por ação enquanto estiver a caminho.
  const once = useSingleFlightRunner();
  const paused = useSessionBlocked();
  const discardWork = useConfirmDiscardWork();
  const user = useAuthStore((s) => s.user);
  const setUser = useAuthStore((s) => s.setUser);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const { data: freshUser, error: profileError, isError: profileFailed, refetch: retryProfile } = useQuery<User>({
    queryKey: ['auth', 'me', 'pending-invitations', user?.tenantId, user?.id],
    queryFn: async () => {
      const epoch = useWorkDrafts.getState().epoch;
      const res = await api.get('/auth/me');
      if (epoch !== useWorkDrafts.getState().epoch) throw new Error('Sessão de acesso alterada.');
      return res.data;
    },
    enabled: !!user && !paused,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });
  const invitations = freshUser?.pendingInvitations ?? user?.pendingInvitations ?? [];

  useEffect(() => {
    if (!freshUser || sessionPaused()) return;
    setUser(freshUser);
    if (!useAuthStore.getState().isAuthenticated) queryClient.clear();
  }, [freshUser, setUser, queryClient]);

  useEffect(() => {
    if (sessionPaused()) return;
    if ((profileError as { response?: { status?: number } })?.response?.status === 401) {
      useAuthStore.getState().expireSession();
      queryClient.clear();
    }
  }, [profileError, queryClient]);

  if (profileFailed && user) return (
    <div role="alert" className="flex flex-wrap items-center gap-3 border-b border-amber-200 bg-amber-50 px-4 py-2 text-so-14 text-amber-900">
      <span>Não foi possível atualizar suas clínicas.</span>
      <button type="button" className="min-h-11 font-bold underline" onClick={() => { void retryProfile(); }}>Tentar novamente</button>
    </div>
  );

  if (invitations.length === 0 || user?.impersonation) return null;

  const acceptInvitation = (membershipId: string) => once(['acceptInvitation', membershipId], async () => {
    setAcceptingId(membershipId);
    try {
      const tenantId = invitations.find((invitation) => invitation.membershipId === membershipId)?.tenant.id;
      await changeCookieSession(queryClient, () => api.post('/auth/membership-invites/accept', { membershipId }), tenantId, (acceptedUser) => {
        navigate(landingFor(acceptedUser.role), { replace: true });
      });
      toast.success('Convite aceito. Empresa ativada com sucesso!');
    } catch (error) {
      if (error instanceof SupersededSessionError) return;
      const e = error as { response?: { data?: { message?: string } } };
      toast.error(e.response?.data?.message || 'Erro ao aceitar convite');
    } finally {
      setAcceptingId(null);
    }
  });

  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-sky-200 bg-sky-50 px-4 py-2 text-so-14 text-sky-950 lg:px-8">
      {discardWork.confirmation}
      <Building2 className="size-4 shrink-0 text-sky-700" />
      <span className="font-bold">Convite pendente</span>
      <span className="text-sky-800">
        Você foi convidado para {invitations.length === 1 ? 'uma empresa' : `${invitations.length} empresas`}.
      </span>
      <div className="ml-auto flex flex-wrap items-center gap-2">
        {invitations.slice(0, 3).map((invitation) => {
          const accepting = acceptingId === invitation.membershipId;
          return (
            <button
              key={invitation.membershipId}
              type="button"
              disabled={!!acceptingId}
              onClick={() => discardWork.run(() => { void acceptInvitation(invitation.membershipId); })}
              className="inline-flex h-8 max-w-[220px] items-center gap-2 rounded-so-sm bg-sky-900 px-3 text-so-12 font-bold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {accepting ? <Loader2 className="size-3.5 animate-spin" /> : <Building2 className="size-3.5" />}
              <span className="truncate">Aceitar {invitation.tenant.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Sober DS (v2) AppShell — 264px sidebar + 1fr content; mobile off-canvas.
 * Scoped to [data-ui="v2"] + .v2-root so the v1 theme is untouched (ADR-2).
 */
export function AppShell({ children }: { children: React.ReactNode }) {
  useDraftUnloadWarning();
  // RC-065: Configurações salvas em outra aba/computador chegam aqui e trocam o fuso da sessão.
  // O token vem do shell em toda tela autenticada (aba recarregada não guarda o token).
  useRealtimeClinicSettings(useSocketToken());
  const paused = useSessionRecovery((state) => state.phase !== 'idle');
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const switching = useSessionTransition((s) => s.isSwitchingTenant);
  const tenantId = useAuthStore((s) => s.user?.tenantId);
  useEffect(() => {
    clearLegacyLocation();
    const params = new URLSearchParams(location.search);
    if (params.has('locationId')) {
      params.delete('locationId');
      navigate({ pathname: location.pathname, search: params.toString(), hash: location.hash }, { replace: true });
    }
  }, [location.pathname, location.search, location.hash, navigate]);
  const isInboxRoute = location.pathname === '/inbox';
  const isTheatreMode = useInboxStore((s) => s.isTheatreMode);
  const compactSidebar = isInboxRoute && isTheatreMode;
  const edgeToEdge = isInboxRoute;

  return (
    <div data-ui="v2" className="v2-root min-h-dvh-safe font-sober">
      <SessionRecoveryDialog />
      <div
        {...(paused || switching ? { inert: '' } : {})}
        className={cn(
          'lg:grid',
          compactSidebar ? 'lg:grid-cols-[72px_minmax(0,1fr)]' : 'lg:grid-cols-[256px_minmax(0,1fr)]',
        )}
      >
        <Sidebar mobileOpen={navOpen} onClose={() => setNavOpen(false)} compact={compactSidebar} />
        <div className="flex min-h-dvh-safe min-w-0 flex-col">
          {!compactSidebar && <Topbar onBurger={() => setNavOpen(true)} />}
          <ImpersonationBanner />
          <PendingInvitationsBanner />
          <DateFixNoticeBanner />
          {/* Story 61.4 — lote de mensagens em andamento (todas as telas, exceto o seletor de clínicas). */}
          {location.pathname.replace(/\/+$/, '').toLowerCase() !== '/clinicas' && <ActiveCampaignBanner />}
          {location.pathname.replace(/\/+$/, '').toLowerCase() !== '/clinicas' && <DocumentSetupNoticeBanner />}
          {location.pathname.replace(/\/+$/, '').toLowerCase() !== '/clinicas' && <UnitsSetupNoticeBanner />}
          {location.pathname.replace(/\/+$/, '').toLowerCase() !== '/clinicas' && <WhatsAppUpgradeNoticeBanner />}
          {/* RC-209 — WhatsApp desconectado com agenda nos próximos 2 dias. */}
          {location.pathname.replace(/\/+$/, '').toLowerCase() !== '/clinicas' && <WhatsAppDisconnectedNoticeBanner />}
          <main key={tenantId} className={cn('min-w-0 flex-1', edgeToEdge ? 'p-0' : 'p-4 lg:p-8')}>{children}</main>
          {/* Story 65.19: "Receber em partes" é desenhado aqui; parado, não renderiza nada. */}
          <ReceiptComposerHost />
        </div>
      </div>
    </div>
  );
}
